import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Undo as ReturnIcon,
} from '@mui/icons-material'
import { Invoice, InvoiceStatus } from '../../../types/payment'
import { InvoiceLineItem } from '../../../types/invoiceTypes'
import { getErrorMessage, INVOICE_ERROR_MESSAGES } from '../../../utils/errorHandlers'
import { calculateLineItemAmounts } from '../../../utils/lineItemCalculations'
import { salesReturnService } from '../../../services/salesReturnService'
import { documentInvoiceService } from '../../../services/documentInvoiceService'
import DocumentAuditHistory from '../../../components/audit/DocumentAuditHistory'
import PaymentRecordDialog from '../../../components/documents/PaymentRecordDialog'
import SalesReturnDialog from '../../../components/documents/SalesReturnDialog'
import { DocumentContextMenu } from '../../../components/documents/DocumentContextMenu'
import InvoicePDFPreview from '../../../components/invoices/InvoicePDFPreview'
import { formatCurrency } from '../../../utils/formatters'
import { format } from 'date-fns'
import api from '../../../services/api'

const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sendingInvoice, setSendingInvoice] = useState(false)
  const [downloadingPDF, setDownloadingPDF] = useState(false)

  // Payment link state
  const [paymentLinkDialog, setPaymentLinkDialog] = useState(false)
  const [generatingLink, setGeneratingLink] = useState(false)
  const [paymentLink, setPaymentLink] = useState<string | null>(null)
  const [linkExpiry, setLinkExpiry] = useState<string>('')
  const [linkDescription, setLinkDescription] = useState<string>('')

  // Payment recording state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)

  // Sales return state
  const [salesReturnDialogOpen, setSalesReturnDialogOpen] = useState(false)

  // Notification state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success'
  })

  useEffect(() => {
    if (id) {
      loadInvoice()
    }
  }, [id])

  const loadInvoice = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await documentInvoiceService.getInvoice(id!)
      setInvoice(data)
      setLinkDescription(`Payment for Invoice ${data.invoice_number}`)
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, INVOICE_ERROR_MESSAGES.LOAD_FAILED)
      setError(errorMessage)
      console.error('Error loading invoice:', err)

      // Show user-friendly notification
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPDF = async (template: string = 'tally_prime') => {
    if (!invoice) return

    try {
      setDownloadingPDF(true)
      const blob = await documentInvoiceService.downloadInvoicePDF(invoice.id, template)

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.invoice_number}_${template}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setSnackbar({
        open: true,
        message: 'PDF downloaded successfully',
        severity: 'success'
      })
    } catch (error: any) {
      console.error('Failed to download PDF:', error)
      setSnackbar({
        open: true,
        message: 'Failed to download PDF',
        severity: 'error'
      })
    } finally {
      setDownloadingPDF(false)
    }
  }

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.DRAFT: return 'default'
      case InvoiceStatus.SENT: return 'info'
      case InvoiceStatus.PAID: return 'success'
      case InvoiceStatus.PARTIAL: return 'warning'
      case InvoiceStatus.OVERDUE: return 'error'
      case InvoiceStatus.CANCELLED: return 'error'
      default: return 'default'
    }
  }

  const handleGeneratePaymentLink = async () => {
    if (!invoice) return

    try {
      setGeneratingLink(true)

      const linkData = {
        invoice_id: invoice.id,
        custom_expiry_hours: linkExpiry ? parseInt(linkExpiry) : undefined,
        description: linkDescription || `Payment for Invoice ${invoice.invoice_number}`,
        notes: {
          invoice_number: invoice.invoice_number,
          customer_name: invoice.customer_name
        }
      }

      const response = await api.post('/admin/razorpay/payment-links', linkData)
      setPaymentLink(response.data.payment_link_url)

      setSnackbar({
        open: true,
        message: 'Payment link generated successfully!',
        severity: 'success'
      })
    } catch (err: any) {
      console.error('Error generating payment link:', err)
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to generate payment link',
        severity: 'error'
      })
    } finally {
      setGeneratingLink(false)
    }
  }

  const handleCopyLink = async () => {
    if (paymentLink) {
      try {
        await navigator.clipboard.writeText(paymentLink)
        setSnackbar({
          open: true,
          message: 'Payment link copied to clipboard!',
          severity: 'success'
        })
      } catch (err) {
        setSnackbar({
          open: true,
          message: 'Failed to copy link',
          severity: 'error'
        })
      }
    }
  }

  const handleSendLinkViaEmail = async () => {
    if (!invoice || !paymentLink) return

    try {
      // This would typically call an API to send email
      // For now, we'll just show a success message
      setSnackbar({
        open: true,
        message: 'Payment link sent via email!',
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send email',
        severity: 'error'
      })
    }
  }

  const handleSendLinkViaSMS = async () => {
    if (!invoice || !paymentLink) return

    try {
      // This would typically call an API to send SMS
      // For now, we'll just show a success message
      setSnackbar({
        open: true,
        message: 'Payment link sent via SMS!',
        severity: 'success'
      })
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to send SMS',
        severity: 'error'
      })
    }
  }

  const closePaymentLinkDialog = () => {
    setPaymentLinkDialog(false)
    setPaymentLink(null)
    setLinkExpiry('')
    setLinkDescription(invoice ? `Payment for Invoice ${invoice.invoice_number}` : '')
  }

  const handleRecordPayment = async (paymentData: any) => {
    if (!invoice) return

    try {
      // Call the backend API to record payment using the payments endpoint
      const response = await fetch(`/api/admin/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          invoice_id: invoice.id,
          payment_date: paymentData.payment_date
            ? (typeof paymentData.payment_date === 'string'
              ? paymentData.payment_date
              : paymentData.payment_date.toISOString().split('T')[0])
            : new Date().toISOString().split('T')[0],
          payment_method: paymentData.payment_method.toLowerCase(),
          amount: paymentData.amount,
          reference_number: paymentData.transaction_id || paymentData.reference_number,
          bank_name: paymentData.bank_name,
          notes: paymentData.notes,
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to record payment')
      }

      const result = await response.json()

      setSnackbar({
        open: true,
        message: `Payment of ₹${paymentData.amount.toLocaleString('en-IN')} recorded successfully. Payment #${result.payment_number || ''}`,
        severity: 'success',
      })

      // Reload the invoice to show updated payment status
      await loadInvoice()
      setPaymentDialogOpen(false)
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to record payment',
        severity: 'error',
      })
      throw error
    }
  }

  const handleCreateSalesReturn = async (returnData: any) => {
    if (!invoice) return

    try {
      const salesReturn = await salesReturnService.createSalesReturn(returnData)

      setSnackbar({
        open: true,
        message: `Sales Return ${salesReturn.return_number} created successfully. Credit Note: ${salesReturn.credit_note_number || 'Pending'}`,
        severity: 'success',
      })

      // Reload the invoice to show updated status
      await loadInvoice()
      setSalesReturnDialogOpen(false)
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create sales return',
        severity: 'error',
      })
      throw error
    }
  }

  const handleSendInvoice = async () => {
    if (!invoice) return

    try {
      setSendingInvoice(true)
      await documentInvoiceService.sendInvoice(invoice.id)

      setSnackbar({
        open: true,
        message: 'Invoice sent successfully to customer',
        severity: 'success',
      })

      // Reload invoice to update status
      await loadInvoice()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send invoice',
        severity: 'error',
      })
    } finally {
      setSendingInvoice(false)
    }
  }



  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="/admin/dashboard">
          Dashboard
        </Link>
        <Link color="inherit" href="/admin/documents/invoices">
          Invoices
        </Link>
        <Typography color="text.primary">
          {invoice.invoice_number}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/admin/documents/invoices')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Invoice {invoice.invoice_number}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1}>
              <Chip
                label={invoice.status.toUpperCase()}
                color={getStatusColor(invoice.status) as any}
                size="small"
              />
              {invoice.sales_order_id && invoice.sales_order_number && (
                <Tooltip title="From sales order">
                  <Chip
                    label={`From ${invoice.sales_order_number}`}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => navigate(`/admin/documents/sales-orders/${invoice.sales_order_id}`)}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          {invoice.status === InvoiceStatus.DRAFT && (
            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={handleSendInvoice}
              disabled={sendingInvoice}
              sx={{ textTransform: 'none' }}
            >
              {sendingInvoice ? 'Sending...' : 'Send Invoice'}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/documents/invoices/${id}/edit`)}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>

          {(invoice.outstanding_amount || invoice.balance_amount || 0) > 0 && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Record Payment
              </Button>
              <Button
                variant="outlined"
                startIcon={<LinkIcon />}
                onClick={() => {
                  setLinkDescription(`Payment for Invoice ${invoice.invoice_number}`)
                  setPaymentLinkDialog(true)
                }}
                sx={{ textTransform: 'none' }}
              >
                Payment Link
              </Button>
            </>
          )}

          {(invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.PARTIAL) && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ReturnIcon />}
              onClick={() => setSalesReturnDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Sales Return
            </Button>
          )}

          <DocumentContextMenu
            documentId={invoice.id}
            documentType="invoice"
            documentNumber={invoice.invoice_number}
            customerEmail={invoice.customer?.email}
            customerPhone={invoice.customer?.phone}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content - Left Side */}
        <Grid item xs={12} md={8}>
          {/* Invoice Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {invoice.invoice_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Date
                  </Typography>
                  <Typography variant="body1">
                    {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1">
                    {invoice.due_date ? format(new Date(invoice.due_date), 'dd/MM/yyyy') : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={invoice.status.toUpperCase()}
                    color={getStatusColor(invoice.status) as any}
                    size="small"
                  />
                </Grid>
                {invoice.sales_order_id && invoice.sales_order_number && (
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      From Sales Order
                    </Typography>
                    <Typography
                      variant="body1"
                      color="primary"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/documents/sales-orders/${invoice.sales_order_id}`)}
                    >
                      {invoice.sales_order_number}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Customer & GST Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Customer & GST Information
              </Typography>
              <Grid container spacing={3}>
                {/* Customer Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Customer Details
                  </Typography>
                  {invoice.customer || invoice.customer_name ? (
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        {invoice.customer?.billing_name || invoice.customer?.name || invoice.customer_name || 'Guest Customer'}
                      </Typography>

                      {invoice.customer?.contact_person && (
                        <Typography variant="body2" color="text.secondary">
                          Contact: {invoice.customer.contact_person}
                        </Typography>
                      )}

                      {(invoice.customer?.email || (invoice.customer && 'email' in invoice && (invoice as any).email)) && (
                        <Typography variant="body2" color="text.secondary">
                          Email: {invoice.customer?.email || (invoice as any).email}
                        </Typography>
                      )}

                      {(invoice.customer?.phone || invoice.customer?.mobile_no) && (
                        <Typography variant="body2" color="text.secondary">
                          Phone: {invoice.customer?.phone || invoice.customer?.mobile_no}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No customer information
                    </Typography>
                  )}
                </Grid>

                {/* GST Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    GST Details
                  </Typography>
                  <Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Supplier GSTIN
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {invoice.supplier_gstin || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Buyer GSTIN
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {invoice.buyer_gstin || (invoice.customer && (invoice.customer as any).gstin) || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Invoice Type
                      </Typography>
                      <Chip
                        label={invoice.invoice_type?.toUpperCase() || 'B2B'}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Addresses */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Addresses
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Billing Address
                  </Typography>
                  {invoice.billing_address ? (
                    <Box>
                      <Typography variant="body2">
                        {invoice.billing_address.address_line1}
                      </Typography>
                      {invoice.billing_address.address_line2 && (
                        <Typography variant="body2">
                          {invoice.billing_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {invoice.billing_address.city}, {invoice.billing_address.state} {invoice.billing_address.postal_code}
                      </Typography>
                      {invoice.billing_address.country && (
                        <Typography variant="body2">
                          {invoice.billing_address.country}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No billing address
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Shipping Address
                  </Typography>
                  {invoice.shipping_address ? (
                    <Box>
                      <Typography variant="body2">
                        {invoice.shipping_address.address_line1}
                      </Typography>
                      {invoice.shipping_address.address_line2 && (
                        <Typography variant="body2">
                          {invoice.shipping_address.address_line2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        {invoice.shipping_address.city}, {invoice.shipping_address.state} {invoice.shipping_address.postal_code}
                      </Typography>
                      {invoice.shipping_address.country && (
                        <Typography variant="body2">
                          {invoice.shipping_address.country}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No shipping address
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Line Items
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>HSN Code</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Taxable Value</TableCell>
                      <TableCell align="right">GST</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.items && invoice.items.length > 0 ? (
                      invoice.items.map((item: InvoiceLineItem, index: number) => {
                        const amounts = calculateLineItemAmounts({
                          quantity: item.quantity,
                          unit_price: item.unit_price,
                          discount_percent: item.discount_percent,
                          tax_rate: item.tax_rate,
                          line_total: item.line_total,
                          discount_amount: item.discount_amount,
                          taxable_amount: item.taxable_amount,
                          tax_amount: item.tax_amount,
                        });

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {item.product_name || 'Product'}
                                </Typography>
                                {item.variant_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.variant_name}
                                  </Typography>
                                )}
                                {item.description && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {item.description}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{item.hsn_code || '-'}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                            <TableCell align="right">
                              {(item.discount_percent || 0) > 0 ? `${item.discount_percent}%` : '-'}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(amounts.taxableValue)}</TableCell>
                            <TableCell align="right">{item.tax_rate || 0}%</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrency(amounts.finalAmount)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No line items available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* GST Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                GST Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier GSTIN
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {invoice.supplier_gstin || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Buyer GSTIN
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {invoice.buyer_gstin || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Type
                  </Typography>
                  <Chip
                    label={invoice.invoice_type?.toUpperCase() || 'B2B'}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notes
                </Typography>
                <Typography variant="body2">
                  {invoice.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Summary - Right Side */}
        <Grid item xs={12} md={4}>
          {/* Financial Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Taxable Value:</Typography>
                <Typography>{formatCurrency(invoice.total_taxable_value || 0)}</Typography>
              </Box>
              {(invoice.cgst_amount || 0) > 0 && (
                <>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>CGST:</Typography>
                    <Typography>{formatCurrency(invoice.cgst_amount || 0)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>SGST:</Typography>
                    <Typography>{formatCurrency(invoice.sgst_amount || 0)}</Typography>
                  </Box>
                </>
              )}
              {(invoice.igst_amount || 0) > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>IGST:</Typography>
                  <Typography>{formatCurrency(invoice.igst_amount || 0)}</Typography>
                </Box>
              )}
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Total Tax:</Typography>
                <Typography>{formatCurrency(invoice.tax_amount || 0)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6" color="primary">
                  Total Amount:
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(invoice.total_amount || 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography color="success.main">Paid:</Typography>
                <Typography color="success.main" fontWeight={600}>
                  {formatCurrency(invoice.paid_amount || 0)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography color={(invoice.balance_amount || 0) > 0 ? 'error.main' : 'success.main'} fontWeight={600}>
                  Balance:
                </Typography>
                <Typography color={(invoice.balance_amount || 0) > 0 ? 'error.main' : 'success.main'} fontWeight={600}>
                  {formatCurrency(invoice.balance_amount || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Invoice History */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Invoice History
              </Typography>
              <Box>
                {invoice.created_at && (
                  <Typography variant="body2" color="text.secondary">
                    Created: {format(new Date(invoice.created_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
                {invoice.updated_at && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Last Updated: {format(new Date(invoice.updated_at), 'dd/MM/yyyy HH:mm')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* PDF Preview */}
          <Box sx={{ mt: 2 }}>
            <InvoicePDFPreview
              invoice={invoice}
              onDownloadPDF={handleDownloadPDF}
            />
          </Box>
        </Grid>

        {/* Audit History */}
        <Grid item xs={12}>
          <DocumentAuditHistory
            documentId={invoice.id}
            documentType="invoice"
            title="Invoice Audit History"
          />
        </Grid>
      </Grid>

      {/* Payment Link Generation Dialog */}
      <Dialog
        open={paymentLinkDialog}
        onClose={closePaymentLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Payment Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Description"
              value={linkDescription}
              onChange={(e) => setLinkDescription(e.target.value)}
              sx={{ mb: 2 }}
              placeholder={`Payment for Invoice ${invoice?.invoice_number || ''}`}
            />
            <TextField
              fullWidth
              label="Link Expiry (Hours)"
              type="number"
              value={linkExpiry}
              onChange={(e) => setLinkExpiry(e.target.value)}
              InputProps={{
                inputProps: { min: 1, max: 720 }
              }}
              helperText="Link will expire after specified hours (max 720 hours = 30 days). Leave empty for default 24 hours."
              sx={{ mb: 2 }}
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              Payment link will be generated for the balance amount: ₹{(invoice.balance_amount || 0).toLocaleString('en-IN')}
            </Alert>

            {paymentLink && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Generated Payment Link:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    value={paymentLink}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <Tooltip title="Copy Link">
                          <IconButton onClick={handleCopyLink} size="small">
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      )
                    }}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<EmailIcon />}
                    onClick={handleSendLinkViaEmail}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Send via Email
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SmsIcon />}
                    onClick={handleSendLinkViaSMS}
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    Send via SMS
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePaymentLinkDialog}>Cancel</Button>
          {!paymentLink && (
            <Button
              variant="contained"
              onClick={handleGeneratePaymentLink}
              disabled={generatingLink}
              startIcon={generatingLink ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {generatingLink ? 'Generating...' : 'Generate Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Payment Recording Dialog */}
      <PaymentRecordDialog
        open={paymentDialogOpen}
        invoice={invoice}
        onClose={() => setPaymentDialogOpen(false)}
        onSubmit={handleRecordPayment}
      />

      {/* Sales Return Dialog */}
      <SalesReturnDialog
        open={salesReturnDialogOpen}
        invoice={{
          id: invoice.id,
          invoice_number: invoice.invoice_number,
          status: invoice.status,
          items: (invoice.items || invoice.line_items || []).map(item => ({
            id: item.id,
            variant_id: item.variant_id || '',
            product_name: item.product_name || 'Unknown Product',
            variant_name: item.variant_name,
            quantity: Number(item.quantity) || 0,
            unit_price: Number(item.unit_price) || 0,
            hsn_code: item.hsn_code,
            tax_rate: Number(item.tax_rate) || 18
          }))
        }}
        onClose={() => setSalesReturnDialogOpen(false)}
        onSubmit={handleCreateSalesReturn}
      />
    </Box>
  )
}

export default InvoiceDetail